import React, { useState, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Button, Card, CardBody, Progress, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";
import { DocumentTypeSchema, TDocument, TDocumentType } from "@shared/entities/document";
import { firebaseApi } from "src/firebase-api";
import { getDownloadURL } from "firebase/storage";
import { deleteField } from "firebase/firestore";

const DocumentUpload: React.FC<{
	label: string;
	description: string;
	fileName?: string;
	folder: string;
	icon: string;
	type: TDocumentType;
	onChange: (file: TDocument | undefined | null) => void;
	error?: string;
	value?: TDocument | null;
	requestId: string;
}> = ({ label, description, icon, onChange, error, fileName, folder, value, type, requestId }) => {
	const [isUploading, setIsUploading] = React.useState(false);
	const [uploadProgress, setUploadProgress] = React.useState(0);
	const [isRemoving, setIsRemoving] = React.useState(false);

	console.log("value", value, type);

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setIsUploading(true);
			// setValue(file);
			setUploadProgress(0);

			// Simulate file upload with progress
			simulateFileUpload(file, (progress) => {
				setUploadProgress(progress);
			});
		}
	};

	const handleRemoveFile = async () => {
		if (!value?.path) return null;
		setIsRemoving(true);
		try {
			// Clear the form field with null instead of undefined
			onChange(null as any);

			// remove from storage
			await firebaseApi.storage.remove(value?.path);
			// remove from request
			await firebaseApi.firestore.update({
				collectionName: "requests",
				item: {
					id: requestId,
					[`documents.${value.type}`]: deleteField(),
				},
			});
		} catch (error) {
			console.error("Error removing file:", error);
			// Handle removal error
		} finally {
			setIsRemoving(false);
		}
	};

	// Simulate file upload with progress
	const simulateFileUpload = async (file: File, onProgress: (progress: number) => void) => {
		const extension = file.name?.split(".").at(-1);

		const name = fileName ? `${fileName}.${extension}` : file.name;

		const task = firebaseApi.storage.uploadFileResumable({
			file,
			folder: folder,
			name: name,
		});
		task.on(
			"state_changed",
			(snapshot) => {
				const progressPercent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
				console.log("progressPercent", progressPercent);

				onProgress(Math.round(progressPercent));
			},
			(err) => {
				console.log("ERRR", err.message);
			},
			async () => {
				const downloadURL = await getDownloadURL(task.snapshot.ref);
				console.log("downloadURL", downloadURL);

				setIsUploading(false);

				onChange({
					createdAt: Date.now(),
					extension: extension as TDocument["extension"],
					fileType: task.snapshot.metadata.contentType as TDocument["fileType"],
					type: type,
					name: name,
					size: task.snapshot.metadata.size,
					url: downloadURL,
					path: `${folder}/${name}`,
					label: file?.name ?? "",
				});
			}
		);
	};

	return (
		<Card
			className={`border-2 border-dashed ${
				error ? "border-danger" : isUploading ? "border-primary" : "border-default-200"
			}`}
		>
			<CardBody className="p-4">
				<div className="flex flex-col items-center justify-center text-center">
					<div
						className={`p-3 rounded-full ${
							error
								? "bg-danger-100"
								: isUploading
								? "bg-primary-100 animate-pulse"
								: "bg-primary-100"
						} mb-3`}
					>
						{isUploading ? (
							<Spinner size="sm" color="primary" />
						) : (
							<Icon
								icon={icon}
								className={`h-6 w-6 ${error ? "text-danger" : "text-primary"}`}
							/>
						)}
					</div>
					<h3 className="font-medium mb-1">{label}</h3>
					<p className="text-default-500 text-sm mb-3">{description}</p>

					{isUploading && (
						<div className="w-full mb-3">
							<Progress
								aria-label="Uploading..."
								value={uploadProgress}
								color="primary"
								className="max-w-md mx-auto"
								showValueLabel={true}
							/>
						</div>
					)}

					{value && !isUploading ? (
						<div className="flex items-center gap-2 p-2 bg-default-100 rounded-medium w-full">
							<Icon icon="lucide:file" className="h-5 w-5 text-primary" />
							<span className="text-sm truncate flex-1">{value?.label ?? fileName}</span>
							<Button
								aria-label="הסר קובץ"
								type="button"
								isIconOnly
								size="sm"
								variant="light"
								color="danger"
								isLoading={isRemoving}
								onPress={handleRemoveFile}
								isDisabled={isRemoving}
							>
								<Icon icon="lucide:x" className="h-4 w-4" />
							</Button>
						</div>
					) : !isUploading ? (
						<div className="w-full">
							<label className="cursor-pointer">
								<div className="flex items-center justify-center gap-2 p-2 bg-default-100 hover:bg-default-200 transition-colors rounded-medium w-full">
									<Icon icon="lucide:upload" className="h-5 w-5 text-default-600" />
									<span className="text-sm">בחר קובץ</span>
								</div>
								<input
									type="file"
									className="hidden"
									accept=".pdf,.jpg,.jpeg,.png"
									onChange={handleFileChange}
									disabled={isUploading || isRemoving}
								/>
							</label>
						</div>
					) : null}

					{error && <p className="text-danger text-sm mt-2">{error}</p>}

					<p className="text-default-400 text-xs mt-3">
						פורמטים מותרים: PDF, JPG, PNG. גודל מקסימלי: 5MB
					</p>
				</div>
			</CardBody>
		</Card>
	);
};

type RequestDocumentKey = Exclude<keyof TRequest["documents"], "PRESCRIPTIONS">;

type TField = {
	name: RequestDocumentKey;
	label: string;
	description: string;
	icon: string;
};

export const DocumentsUploadForm: React.FC = () => {
	const {
		control,
		formState: { errors },
		watch,
	} = useFormContext<TRequest>();

	const requestType = watch("requestType");
	const requestId = watch("id");
	const request = watch();
	const documents = watch("documents");
	console.log("requestType", requestType);
	console.log("request", request);

	// Initialize optionalFiles based on existing custom documents in the form
	const initializeOptionalFiles = (): Array<TField> => {
		const existingCustomDocs: Array<TField> = [];

		// Check for existing CUSTOM1, CUSTOM2, CUSTOM3 documents
		const customKeys: Array<"CUSTOM1" | "CUSTOM2" | "CUSTOM3"> = ["CUSTOM1", "CUSTOM2", "CUSTOM3"];
		for (const customKey of customKeys) {
			const documentKey = DocumentTypeSchema.Values[customKey] as RequestDocumentKey;

			if (documents?.[documentKey]) {
				existingCustomDocs.push({
					name: documentKey,
					label: `מסמך נוסף ${customKey.slice(-1)}`,
					description: "מסמך נוסף",
					icon: "lucide:clipboard-list",
				});
			}
		}

		return existingCustomDocs;
	};

	const [optionalFiles, setOptionalFiles] = useState<Array<TField>>(initializeOptionalFiles);

	// Sync optionalFiles with existing documents when documents change
	useEffect(() => {
		const existingCustomDocs = initializeOptionalFiles();
		setOptionalFiles(existingCustomDocs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documents]);

	const requiredDocuments: Array<TField> = [
		{
			name: DocumentTypeSchema.Values.ID,
			label: "תעודת זהות",
			description: "צילום תעודת זהות כולל ספח",
			icon: "lucide:id-card",
		},
		{
			name: DocumentTypeSchema.Values.MEDICAL_SUMMARY,
			label: "סיכום רפואי",
			description: "סיכום רפואי מעודכן",
			icon: "lucide:clipboard-list",
		},
		{
			name: DocumentTypeSchema.Values.DOCTOR_RECOMMENDATION,
			label: "המלצת רופא",
			description: "המלצת רופא לשימוש בקנאביס רפואי",
			icon: "lucide:stethoscope",
		},
	];

	if (requestType === "renew") {
		requiredDocuments.push({
			name: DocumentTypeSchema.Values.MEDICATION_REPORT as any,
			label: "דוח תרופות",
			description: "דוח תרופות",
			icon: "lucide:stethoscope",
		});
		requiredDocuments.push({
			name: DocumentTypeSchema.Values.CANNABIS_LICENSE as any,
			label: "רשיון קנאביס",
			description: "רשיון קנאביס",
			icon: "lucide:stethoscope",
		});
	}

	if (requestType === "new") {
		requiredDocuments.push({
			name: DocumentTypeSchema.Values.MEDICATION_REPORT as any,
			label: "דוח תרופות",
			description: "דוח תרופות",
			icon: "lucide:stethoscope",
		});
	}
	if (requestType === "increase") {
		requiredDocuments.push(
			{
				name: DocumentTypeSchema.Values.MEDICATION_REPORT as any,
				label: "דוח תרופות",
				description: "דוח תרופות",
				icon: "lucide:stethoscope",
			},
			{
				name: DocumentTypeSchema.Values.CONSUMPTION_TABLE as any,
				label: "טבלת צריכה",
				description: "טבלת צריכה",
				icon: "lucide:stethoscope",
			}
		);
		requiredDocuments.push({
			name: DocumentTypeSchema.Values.CANNABIS_LICENSE as any,
			label: "רשיון קנאביס",
			description: "רשיון קנאביס",
			icon: "lucide:stethoscope",
		});
	}

	console.log("documents", documents);

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-3 mb-4">
				<div className="bg-warning-100 p-2 rounded-full">
					<Icon icon="lucide:file-text" className="h-6 w-6 text-warning" />
				</div>
				<div>
					<h2 className="text-xl font-semibold">העלאת מסמכים</h2>
					<p className="text-default-500 text-sm">העלה את המסמכים הנדרשים לבקשה</p>
				</div>
				<Button
					type="button"
					isDisabled={optionalFiles.length >= 3}
					onPress={() => {
						// Find the next available slot (1, 2, or 3)
						const existingSlots = optionalFiles.map((doc) => {
							const match = doc.name.match(/CUSTOM(\d+)/);
							return match ? parseInt(match[1]) : 0;
						});

						let nextSlot = 1;
						while (existingSlots.includes(nextSlot) && nextSlot <= 3) {
							nextSlot++;
						}

						if (nextSlot <= 3) {
							const customKeys: Record<number, "CUSTOM1" | "CUSTOM2" | "CUSTOM3"> = {
								1: "CUSTOM1",
								2: "CUSTOM2",
								3: "CUSTOM3",
							};
							const customKey = customKeys[nextSlot];
							const documentKey = DocumentTypeSchema.Values[customKey] as RequestDocumentKey;

							const newField: TField = {
								name: documentKey,
								label: `מסמך נוסף ${nextSlot}`,
								description: "מסמך נוסף",
								icon: "lucide:clipboard-list",
							};

							setOptionalFiles((prev) => [...prev, newField]);
						}
					}}
				>
					הוסף קובץ
				</Button>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{requiredDocuments.map((doc) => (
					<Controller
						key={doc.name}
						name={`documents.${doc.name}`}
						control={control}
						render={({ field }) => {
							console.log("field", field);
							return (
								<DocumentUpload
									fileName={doc.name}
									type={doc.name}
									folder={`requests/${requestId}`}
									label={doc.label}
									description={doc.description}
									icon={doc.icon}
									onChange={field.onChange}
									value={field.value}
									error={errors.documents?.[doc.name]?.message?.toString()}
									requestId={requestId}
								/>
							);
						}}
					/>
				))}

				{optionalFiles.map((doc, index) => (
					<Controller
						key={doc.name}
						name={`documents.${doc.name}`}
						control={control}
						render={({ field }) => (
							<Card className="relative">
								<CardBody>
									<DocumentUpload
										fileName={doc.name}
										type={doc.name}
										folder={`requests/${requestId}`}
										label={doc.label}
										description={doc.description}
										icon={doc.icon}
										onChange={field.onChange}
										value={field.value}
										error={errors.documents?.[doc.name]?.message?.toString()}
										requestId={requestId}
									/>
									<Button
										aria-label="הסר קובץ"
										isIconOnly
										size="sm"
										color="danger"
										variant="light"
										className="absolute top-2 right-2"
										onPress={() => {
											// Clear the form field with null instead of undefined
											field.onChange(null);
											// Remove from optionalFiles state
											setOptionalFiles(optionalFiles.filter((_, i) => i !== index));
										}}
									>
										<Icon icon="lucide:x" className="h-4 w-4" />
									</Button>
								</CardBody>
							</Card>
						)}
					/>
				))}
			</div>

			<div className="p-4 bg-warning-50 border border-warning-200 rounded-medium">
				<div className="flex items-start gap-3">
					<Icon icon="lucide:alert-triangle" className="h-5 w-5 text-warning mt-0.5" />
					<div>
						<h3 className="font-medium text-warning-700 mb-1">חשוב לדעת</h3>
						<p className="text-sm text-warning-700">
							כל המסמכים חייבים להיות קריאים וברורים. מסמכים לא תקינים יעכבו את הטיפול בבקשה.
							אנא ודא שכל המסמכים הנדרשים הועלו בפורמט המתאים ובגודל המתאים.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
